import type { FC } from "react"
import React from "react"

import { MODULE_POWUR_OS_EQUIPMENT_ID } from "@logic/lib/powur-os"
import { useTranslation } from "react-i18next"
import { useRecoilValue, useSetRecoilState } from "recoil"
import type { FlattenSimpleInterpolation } from "styled-components"
import styled from "styled-components"

import type { Design } from "~/containers/Proposal/state/designs/design-model"
import { designLocalState } from "~/containers/Proposal/state/designs/design-state"
import { getSolarModuleEquipmentFromDesign } from "~/containers/Proposal/state/designs/solar-module"
import { useFetchEquipmentForSiteContext } from "~/containers/Proposal/state/equipment/use-equipment"
import { useProposalContext } from "~/containers/Proposal/state/proposal/proposal-context"
import { Dropdown } from "~/ui/lib/options/Select/Dropdown"
import type { NewEquipment as EquipmentTypeInferredFromZod } from "~/utils/finance/design-types"
import { formatCurrency } from "~/utils/finance/format"
import { isPowurLineProject } from "~/utils/powurline-utils"

const createModulePricePerWatt = (item: EquipmentTypeInferredFromZod) => {
  if (!item?.wattage) {
    return `-`
  }

  return formatCurrency((item.currentProposalPrice ?? 0) / item.wattage)
}

const createModuleName = (
  item: EquipmentTypeInferredFromZod,
  isPowurLine = false,
) => {
  if (!item) {
    return `None`
  }
  if (!isPowurLine && item?.isDefault) {
    return item.name + ` (Recommended)`
  }

  return item.name
}

const ModuleSelect_Internal = ({
  isDisabled,
  wrapperStyle,
}: {
  isDisabled?: boolean
  wrapperStyle?: FlattenSimpleInterpolation
}) => {
  const design = useRecoilValue(designLocalState)
  const setDesign = useSetRecoilState(designLocalState)
  const currentModule = React.useMemo(() => {
    const solarModule = getSolarModuleEquipmentFromDesign(design)

    return solarModule
      ? [solarModule as unknown as EquipmentTypeInferredFromZod]
      : []
  }, [design])
  const setCurrentModule = React.useCallback(
    (solarModule: EquipmentTypeInferredFromZod) => {
      setDesign((currentDesign) => ({
        ...currentDesign,
        designEquipment: [
          ...(currentDesign.designEquipment ?? []).filter(
            (designEquipment) =>
              designEquipment.powurOsEquipmentTypeId !==
              MODULE_POWUR_OS_EQUIPMENT_ID,
          ),
          {
            createdAt: new Date(0),
            designId: currentDesign.id,
            equipment: solarModule as unknown as NonNullable<
              Design[`designEquipment`]
            >[number][`equipment`],
            equipmentId: solarModule.id,
            powurOsEquipmentTypeId: MODULE_POWUR_OS_EQUIPMENT_ID,
            id: `selected-module-${solarModule.id}`,
            price: solarModule.currentProposalPrice ?? 0,
            quantity: 1,
            updatedAt: new Date(0),
          },
        ],
      }))
    },
    [setDesign],
  )
  const { solarModules, solarModulesHaveLoaded, refetch } =
    useFetchEquipmentForSiteContext()
  const proposal = useProposalContext()
  const { t } = useTranslation()
  const isPowurLine = isPowurLineProject(proposal.remote.enterpriseSettings)

  // Refetch equipment data when component mounts
  React.useEffect(() => {
    refetch()
  }, [refetch])

  return (
    <Dropdown.Container
      SelectedOption={() => (
        <>
          <p>{currentModule[0]?.name ?? `None`}</p>
          <span>{createModulePricePerWatt(currentModule[0])}/W</span>
        </>
      )}
      wrapperStyle={wrapperStyle}
      disabled={isDisabled}
      Label={() => <label>{t(`module`)}</label>}
      Options={(solarModulesHaveLoaded ? solarModules : []).map((item) => {
        return (
          <Dropdown.Item
            label1={createModuleName(item, isPowurLine)}
            key={item.id}
            label2={`${createModulePricePerWatt(item)}/W`}
            onClick={() => {
              setCurrentModule(item)
            }}
            isChecked={item.id === currentModule[0]?.id}
          />
        )
      })}
    />
  )
}

export const ModuleSelect = React.memo(ModuleSelect_Internal)

export const DEPRECATED_ModuleSelectionDropdown: FC = () => {
  return (
    <Container>
      <ModuleSelect />
    </Container>
  )
}

export const Container = styled.div`
  cursor: pointer;
  > p {
    cursor: initial;
    margin-bottom: 5px;
  }
`
